export type AiBuilderQuestionSetQuestion = {
  questionId: string;
  selectionMode: 'single' | 'multiple';
  allowDirectInput: boolean;
};

export type AiBuilderQuestionSetChoiceAnswer = {
  mode: 'choice';
  optionIds: readonly string[];
};

export type AiBuilderQuestionSetTextAnswer = {
  mode: 'text';
  text: string;
};

export type AiBuilderQuestionSetAnswer =
  | AiBuilderQuestionSetChoiceAnswer
  | AiBuilderQuestionSetTextAnswer;

export type AiBuilderQuestionSetState<
  Question extends AiBuilderQuestionSetQuestion,
> = {
  interactionId: string;
  activeQuestionId: string;
  questions: readonly Question[];
  questionDrafts: Partial<Record<string, AiBuilderQuestionSetAnswer>>;
  questionAnswers: Partial<Record<string, AiBuilderQuestionSetAnswer>>;
  cancelled: boolean;
  submissionError: string | null;
};

export type AiBuilderQuestionSetAction =
  | { type: 'navigatePrevious' }
  | { type: 'navigateNext' }
  | { type: 'selectSingleChoice'; questionId: string; optionId: string }
  | {
      type: 'updateMultipleChoices';
      questionId: string;
      optionIds: readonly string[];
    }
  | { type: 'updateDirectText'; questionId: string; text: string }
  | { type: 'confirmAnswer'; questionId: string }
  | { type: 'cancel' }
  | { type: 'setSubmissionError'; error: string }
  | { type: 'clearSubmissionError' };

export const createAiBuilderQuestionSetState = <
  Question extends AiBuilderQuestionSetQuestion,
>({
  interactionId,
  questions,
  activeQuestionId = questions[0]?.questionId,
  questionDrafts = {},
  questionAnswers = {},
  cancelled = false,
  submissionError = null,
}: {
  interactionId: string;
  questions: readonly Question[];
  activeQuestionId?: string;
  questionDrafts?: Partial<Record<string, AiBuilderQuestionSetAnswer>>;
  questionAnswers?: Partial<Record<string, AiBuilderQuestionSetAnswer>>;
  cancelled?: boolean;
  submissionError?: string | null;
}): AiBuilderQuestionSetState<Question> => {
  const validActiveQuestionId = assertQuestionSetInvariants({
    activeQuestionId,
    questions,
  });

  const knownQuestionIds = new Set(
    questions.map(question => question.questionId),
  );

  return {
    interactionId,
    activeQuestionId: validActiveQuestionId,
    questions,
    questionDrafts: filterKnownQuestionValues(questionDrafts, knownQuestionIds),
    questionAnswers: normalizeConfirmedAnswers(questions, questionAnswers),
    cancelled,
    submissionError,
  };
};

export const reduceAiBuilderQuestionSetState = <
  Question extends AiBuilderQuestionSetQuestion,
>(
  state: AiBuilderQuestionSetState<Question>,
  action: AiBuilderQuestionSetAction,
): AiBuilderQuestionSetState<Question> => {
  if (state.cancelled) return state;

  switch (action.type) {
    case 'navigatePrevious':
      return navigateByOffset(state, -1);
    case 'navigateNext':
      return navigateByOffset(state, 1);
    case 'selectSingleChoice':
      return selectSingleChoice(state, action.questionId, action.optionId);
    case 'updateMultipleChoices':
      return updateMultipleChoices(state, action.questionId, action.optionIds);
    case 'updateDirectText':
      return updateDirectText(state, action.questionId, action.text);
    case 'confirmAnswer':
      return confirmQuestionAnswer(state, action.questionId);
    case 'cancel':
      return {
        ...state,
        questionDrafts: {},
        questionAnswers: {},
        cancelled: true,
        submissionError: null,
      };
    case 'setSubmissionError':
      return { ...state, submissionError: action.error };
    case 'clearSubmissionError':
      return { ...state, submissionError: null };
  }
};

export const getAnsweredQuestionIds = <
  Question extends AiBuilderQuestionSetQuestion,
>(
  state: AiBuilderQuestionSetState<Question>,
): readonly string[] =>
  state.questions
    .map(question => question.questionId)
    .filter(questionId => state.questionAnswers[questionId] !== undefined);

export const canSubmitAiBuilderQuestionSet = <
  Question extends AiBuilderQuestionSetQuestion,
>(
  state: AiBuilderQuestionSetState<Question>,
): boolean =>
  !state.cancelled &&
  state.questions.every(
    question =>
      normalizeConfirmedAnswer(
        question,
        state.questionAnswers[question.questionId],
      ) !== null,
  );

export const getNextUnansweredQuestionId = <
  Question extends AiBuilderQuestionSetQuestion,
>(
  state: AiBuilderQuestionSetState<Question>,
  fromQuestionId: string,
): string | null => {
  const fromIndex = findQuestionIndex(state.questions, fromQuestionId);
  if (fromIndex === -1) return null;

  for (let offset = 1; offset < state.questions.length; offset += 1) {
    const question =
      state.questions[(fromIndex + offset) % state.questions.length];

    if (question && state.questionAnswers[question.questionId] === undefined) {
      return question.questionId;
    }
  }

  return null;
};

const navigateByOffset = <Question extends AiBuilderQuestionSetQuestion>(
  state: AiBuilderQuestionSetState<Question>,
  offset: -1 | 1,
): AiBuilderQuestionSetState<Question> => {
  const activeIndex = findQuestionIndex(
    state.questions,
    state.activeQuestionId,
  );
  const nextQuestion = state.questions[activeIndex + offset];

  if (!nextQuestion) return state;

  return {
    ...state,
    activeQuestionId: nextQuestion.questionId,
  };
};

const selectSingleChoice = <Question extends AiBuilderQuestionSetQuestion>(
  state: AiBuilderQuestionSetState<Question>,
  questionId: string,
  optionId: string,
): AiBuilderQuestionSetState<Question> => {
  const question = findQuestion(state.questions, questionId);
  if (!question || question.selectionMode !== 'single' || !optionId) {
    return state;
  }

  return confirmDraft(state, questionId, {
    mode: 'choice',
    optionIds: [optionId],
  });
};

const updateMultipleChoices = <Question extends AiBuilderQuestionSetQuestion>(
  state: AiBuilderQuestionSetState<Question>,
  questionId: string,
  optionIds: readonly string[],
): AiBuilderQuestionSetState<Question> => {
  const question = findQuestion(state.questions, questionId);
  if (!question || question.selectionMode !== 'multiple') return state;

  return updateQuestionDraft(state, questionId, {
    mode: 'choice',
    optionIds: [...new Set(optionIds.filter(Boolean))],
  });
};

const updateDirectText = <Question extends AiBuilderQuestionSetQuestion>(
  state: AiBuilderQuestionSetState<Question>,
  questionId: string,
  text: string,
): AiBuilderQuestionSetState<Question> => {
  const question = findQuestion(state.questions, questionId);
  if (!question?.allowDirectInput) return state;

  return updateQuestionDraft(state, questionId, { mode: 'text', text });
};

const updateQuestionDraft = <Question extends AiBuilderQuestionSetQuestion>(
  state: AiBuilderQuestionSetState<Question>,
  questionId: string,
  draft: AiBuilderQuestionSetAnswer,
): AiBuilderQuestionSetState<Question> => {
  const { [questionId]: _previousAnswer, ...otherAnswers } =
    state.questionAnswers;

  return {
    ...state,
    questionDrafts: { ...state.questionDrafts, [questionId]: draft },
    questionAnswers: otherAnswers,
  };
};

const confirmQuestionAnswer = <Question extends AiBuilderQuestionSetQuestion>(
  state: AiBuilderQuestionSetState<Question>,
  questionId: string,
): AiBuilderQuestionSetState<Question> => {
  const question = findQuestion(state.questions, questionId);
  const draft = state.questionDrafts[questionId];
  if (!question || !draft || !isConfirmableAnswer(question, draft)) {
    return state;
  }

  return confirmDraft(state, questionId, draft);
};

const confirmDraft = <Question extends AiBuilderQuestionSetQuestion>(
  state: AiBuilderQuestionSetState<Question>,
  questionId: string,
  answer: AiBuilderQuestionSetAnswer,
): AiBuilderQuestionSetState<Question> => {
  const nextState = {
    ...state,
    questionDrafts: { ...state.questionDrafts, [questionId]: answer },
    questionAnswers: { ...state.questionAnswers, [questionId]: answer },
    submissionError: null,
  };
  const nextUnansweredQuestionId = getNextUnansweredQuestionId(
    nextState,
    questionId,
  );

  return {
    ...nextState,
    activeQuestionId: nextUnansweredQuestionId ?? questionId,
  };
};

const isConfirmableAnswer = (
  question: AiBuilderQuestionSetQuestion,
  answer: AiBuilderQuestionSetAnswer,
): boolean => normalizeConfirmedAnswer(question, answer) !== null;

const findQuestionIndex = <Question extends AiBuilderQuestionSetQuestion>(
  questions: readonly Question[],
  questionId: string,
) => questions.findIndex(question => question.questionId === questionId);

const findQuestion = <Question extends AiBuilderQuestionSetQuestion>(
  questions: readonly Question[],
  questionId: string,
) => questions.find(question => question.questionId === questionId);

const filterKnownQuestionValues = <Value>(
  values: Partial<Record<string, Value>>,
  knownQuestionIds: ReadonlySet<string>,
): Partial<Record<string, Value>> =>
  Object.fromEntries(
    Object.entries(values).filter(([questionId]) =>
      knownQuestionIds.has(questionId),
    ),
  ) as Partial<Record<string, Value>>;

const normalizeConfirmedAnswers = <
  Question extends AiBuilderQuestionSetQuestion,
>(
  questions: readonly Question[],
  answers: Partial<Record<string, AiBuilderQuestionSetAnswer>>,
): Partial<Record<string, AiBuilderQuestionSetAnswer>> =>
  Object.fromEntries(
    questions.flatMap(question => {
      const answer = normalizeConfirmedAnswer(
        question,
        answers[question.questionId],
      );
      return answer ? [[question.questionId, answer] as const] : [];
    }),
  );

const normalizeConfirmedAnswer = (
  question: AiBuilderQuestionSetQuestion,
  answer: unknown,
): AiBuilderQuestionSetAnswer | null => {
  if (!answer || typeof answer !== 'object') return null;

  if (
    'mode' in answer &&
    answer.mode === 'choice' &&
    'optionIds' in answer &&
    Array.isArray(answer.optionIds) &&
    !('text' in answer)
  ) {
    const optionIds = [
      ...new Set(
        answer.optionIds.filter(
          (optionId): optionId is string =>
            typeof optionId === 'string' && optionId.trim().length > 0,
        ),
      ),
    ];
    const hasValidCardinality =
      question.selectionMode === 'single'
        ? optionIds.length === 1
        : optionIds.length > 0;

    return hasValidCardinality ? { mode: 'choice', optionIds } : null;
  }

  if (
    'mode' in answer &&
    answer.mode === 'text' &&
    'text' in answer &&
    typeof answer.text === 'string' &&
    !('optionIds' in answer) &&
    question.allowDirectInput &&
    answer.text.trim().length > 0
  ) {
    return { mode: 'text', text: answer.text };
  }

  return null;
};

const assertQuestionSetInvariants = <
  Question extends AiBuilderQuestionSetQuestion,
>({
  activeQuestionId,
  questions,
}: {
  activeQuestionId: string | undefined;
  questions: readonly Question[];
}): string => {
  if (questions.length === 0) {
    throw new Error(
      'AI builder question set must contain at least one question.',
    );
  }

  const questionIds = questions.map(question => question.questionId);
  if (new Set(questionIds).size !== questionIds.length) {
    throw new Error('AI builder question ids must be unique.');
  }

  if (!activeQuestionId || !questionIds.includes(activeQuestionId)) {
    throw new Error(
      'AI builder active question must belong to the question set.',
    );
  }

  return activeQuestionId;
};
